import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import { logout } from '../GlobalAction';
import { ICoach } from '../coach/coachSlice';

export interface ITherapist {
  id: string;
  approvalId?: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  status: string;
  idpId: string;
  language: string;
  tp: string;
  organizationId: string;
  clientDescription: string;
  coachTypes: string[];
  therapyTypes: string[];
  profilePic: File | null;
  timeZone: string;
  subscribeToNewsletter?: boolean;
  isAgreedPrivacyPolicy?: boolean;
}

export interface ICreateCoach {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  timeZone: string;
  organizationId: string;
  isAgreedPrivacyPolicy: boolean;
  password: string;
  confirmPassword: string;
  subscribeToNewsletter: boolean;
  brandTag?: string;
}

export interface IVerifyEmail {
  coachId: string;
  verificationCode: string;
}

export interface ICoachTypes {
  coachType: string;
  description: string[];
}

interface ICoachOnboarding {
  id: string;
  timeZone: string;
  coachTypes: ICoachTypes[];
  profilePic: string;
  isAgreedPrivacyPolicy: boolean;
  subscribeToNewsletter: boolean;
  isExternalImage: boolean;
}

export interface ICoachCreateAccount {
  CoachOnboarding: ICoachOnboarding;
  profilePic: File | null;
}

export interface ICoachOnboardingResponse {
  isPrincipalCoach: boolean;
}

interface IOnboardingState {
  profilePic: File | null;
  therapist: ITherapist;
  addTherapistLoading: boolean;
  addTherapistSuccess: boolean;
  error: {
    hasError: boolean;
    message: string;
  };
  verifyEmailError: {
    hasError: boolean;
    message: string;
  };
  createCoachLoading: boolean;
  onboardingSuccesModalOpen: boolean;
  activeStep: number;
  isPrincipalCoach: boolean;
}

const initialState: IOnboardingState = {
  profilePic: null,
  therapist: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    isActive: false,
    status: '',
    idpId: '',
    language: '',
    tp: '',
    coachTypes: [],
    profilePic: null,
    timeZone: '',
    therapyTypes: [],
    organizationId: '',
    clientDescription: '',
  },
  addTherapistLoading: false,
  addTherapistSuccess: false,
  error: {
    hasError: false,
    message: '',
  },
  createCoachLoading: false,
  onboardingSuccesModalOpen: false,
  verifyEmailError: {
    hasError: false,
    message: '',
  },
  activeStep: 0,
  isPrincipalCoach: true,
};

const onboardingSlice = createSlice({
  name: 'note',
  initialState,
  reducers: {
    setProfilePic(state, action: PayloadAction<File | null>) {
      state.therapist.profilePic = action.payload;
    },
    setCoachTypes(state, action: PayloadAction<string[]>) {
      state.therapist.coachTypes = action.payload;
    },
    setTherapyTypes(state, action: PayloadAction<string[]>) {
      state.therapist.therapyTypes = action.payload;
    },
    createCoach(
      state,
      _action: PayloadAction<{
        data: ICreateCoach;
        navigate: NavigateFunction;
      }>
    ) {
      state.createCoachLoading = true;
    },
    createCoachSuccess(state, action: PayloadAction<ITherapist>) {
      state.createCoachLoading = false;
      state.therapist = action.payload;
    },
    createCoachFailure(state, action: PayloadAction<string>) {
      state.createCoachLoading = false;
      state.error = {
        hasError: true,
        message: action.payload,
      };
    },
    verifyEmail(
      _state,
      _action: PayloadAction<{ data: IVerifyEmail; navigate: NavigateFunction }>
    ) {},
    verifyEmailFailure(state, action: PayloadAction<string>) {
      state.verifyEmailError = {
        hasError: true,
        message: action.payload,
      };
    },
    verifyEmailFailureReset(state) {
      state.verifyEmailError = {
        hasError: false,
        message: '',
      };
    },
    createCoachAccount(state, _action: PayloadAction<{ data: ICoachCreateAccount }>) {
      state.addTherapistLoading = true;
    },
    createCoachAccountSuccess(state, _action: PayloadAction<ICoach>) {
      state.addTherapistSuccess = true;
      state.addTherapistLoading = false;
      state.isPrincipalCoach = _action.payload.isPrincipalCoach || false;
    },
    createCoachAccountFailure(state, action: PayloadAction<string>) {
      state.addTherapistSuccess = false;
      state.addTherapistLoading = false;
      state.error = {
        hasError: true,
        message: action.payload,
      };
    },
    resendVerificationCode(_state, _action: PayloadAction<{ coachId: string }>) {},
    resendVerificationCodeFailure(state, action: PayloadAction<string>) {
      state.error = {
        hasError: true,
        message: action.payload,
      };
    },
    openOnboardingSuccessModal(state) {
      state.onboardingSuccesModalOpen = true;
    },
    closeOnboardingSuccessModal(state) {
      state.onboardingSuccesModalOpen = false;
    },
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

const onboardingActions = onboardingSlice.actions;
const onboardingReducer = onboardingSlice.reducer;

export { onboardingActions, onboardingReducer };
